import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { Badge } from "@workspace/amarmeds-ds/components/ui/badge";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@workspace/amarmeds-ds/components/ui/card";
import { Input } from "@workspace/amarmeds-ds/components/ui/input";
import { Label } from "@workspace/amarmeds-ds/components/ui/label";

import { api, ApiError, type OpsPharmacy, type OpsZone } from "../api";

const pharmacyBody = (p: { name: string; zoneKey: string; opensAt: string; closesAt: string; licenceNo: string; active: boolean }) => ({
  name: p.name,
  zoneKey: p.zoneKey,
  opensAt: p.opensAt,
  closesAt: p.closesAt,
  licenceNo: p.licenceNo,
  active: p.active,
});

// Add and edit pharmacies from the dashboard, and open their price lists.
export default function OpsPharmacies() {
  const qc = useQueryClient();
  const pharmacies = useQuery({ queryKey: ["ops-pharmacies"], queryFn: () => api<{ pharmacies: OpsPharmacy[] }>("/ops/pharmacies") });
  const zones = useQuery({ queryKey: ["ops-zones"], queryFn: () => api<{ zones: OpsZone[] }>("/ops/zones") });

  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [zoneKey, setZoneKey] = useState("");
  const [opensAt, setOpensAt] = useState("09:00");
  const [closesAt, setClosesAt] = useState("22:00");
  const [licenceNo, setLicenceNo] = useState("");
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setEditingId(null);
    setName("");
    setZoneKey("");
    setOpensAt("09:00");
    setClosesAt("22:00");
    setLicenceNo("");
    setActive(true);
    setError(null);
  };

  const save = useMutation({
    mutationFn: () =>
      api(
        editingId === null ? "/ops/pharmacies" : `/ops/pharmacies/${editingId}`,
        { method: editingId === null ? "POST" : "PUT", body: { name, zoneKey, opensAt, closesAt, licenceNo, active } },
      ),
    onSuccess: () => {
      reset();
      void qc.invalidateQueries({ queryKey: ["ops-pharmacies"] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.code.replace(/_/g, " ") : "Could not save the pharmacy."),
  });

  const toggle = useMutation({
    mutationFn: (p: OpsPharmacy) => api(`/ops/pharmacies/${p.id}`, { method: "PUT", body: pharmacyBody({ ...p, active: !p.active }) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ops-pharmacies"] }),
  });

  const [listMessage, setListMessage] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: (p: OpsPharmacy) => api<{ prices: number; staff: number }>(`/ops/pharmacies/${p.id}`, { method: "DELETE" }),
    onSuccess: (res, p) => {
      setListMessage(`Deleted ${p.name}, with ${res.prices} prices and ${res.staff} counter sign-in${res.staff === 1 ? "" : "s"}.`);
      if (editingId === p.id) reset();
      void qc.invalidateQueries({ queryKey: ["ops-pharmacies"] });
      void qc.invalidateQueries({ queryKey: ["ops-staff"] });
    },
    onError: (err, p) =>
      setListMessage(
        err instanceof ApiError && err.code === "pharmacy_has_orders"
          ? `${p.name} has ${Number(err.body.orders)} order(s) in its history, so it cannot be deleted. Deactivate it instead.`
          : err instanceof ApiError
            ? err.code.replace(/_/g, " ")
            : "Could not delete the pharmacy.",
      ),
  });
  const askDelete = (p: OpsPharmacy) => {
    if (window.confirm(`Delete ${p.name} for good? Its price list (${p.priceCount ?? 0} prices) and its counter staff sign-ins go with it. This cannot be undone.`)) remove.mutate(p);
  };

  const edit = (p: OpsPharmacy) => {
    setEditingId(p.id);
    setName(p.name);
    setZoneKey(p.zoneKey);
    setOpensAt(p.opensAt);
    setClosesAt(p.closesAt);
    setLicenceNo(p.licenceNo);
    setActive(p.active);
    setError(null);
  };

  const ready = name.trim().length > 0 && zoneKey !== "" && /^([01]\d|2[0-3]):[0-5]\d$/.test(opensAt) && /^([01]\d|2[0-3]):[0-5]\d$/.test(closesAt);
  const zoneList = zones.data?.zones ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Pharmacies</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{editingId === null ? "Add a pharmacy" : `Edit pharmacy #${editingId}`}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="ph-name">Name</Label>
              <Input id="ph-name" placeholder="Pilot Pharmacy" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ph-zone">Zone</Label>
              <select id="ph-zone" className="min-h-9 w-full rounded-md border bg-background px-2" value={zoneKey} onChange={(e) => setZoneKey(e.target.value)}>
                <option value="">Choose a zone</option>
                {zoneList.map((z) => (
                  <option key={z.key} value={z.key}>
                    {z.name} ({z.key}){z.active ? "" : " · inactive"}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ph-open">Opens</Label>
              <Input id="ph-open" type="time" value={opensAt} onChange={(e) => setOpensAt(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ph-close">Closes</Label>
              <Input id="ph-close" type="time" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ph-licence">Licence no.</Label>
              <Input id="ph-licence" placeholder="DGDA-123" value={licenceNo} onChange={(e) => setLicenceNo(e.target.value)} />
            </div>
            <label className="flex items-end gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={active} onChange={(e) => setActive(e.target.checked)} />
              Active (takes orders)
            </label>
          </div>
          <div className="flex items-center gap-3">
            <Button disabled={!ready || save.isPending} onClick={() => save.mutate()}>
              {editingId === null ? "Add pharmacy" : "Save changes"}
            </Button>
            {editingId !== null && (
              <Button variant="outline" onClick={reset}>
                Cancel
              </Button>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pharmacies</CardTitle>
        </CardHeader>
        <CardContent>
          {pharmacies.isLoading && <p className="text-muted-foreground">Loading…</p>}
          {listMessage && <p className="pb-2 text-sm">{listMessage}</p>}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="p-2">ID</th>
                  <th className="p-2">Name</th>
                  <th className="p-2">Zone</th>
                  <th className="p-2">Hours</th>
                  <th className="p-2">Licence</th>
                  <th className="p-2">Prices</th>
                  <th className="p-2">Status</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {pharmacies.data?.pharmacies.map((p) => (
                  <tr key={p.id} className="border-t align-top">
                    <td className="p-2">{p.id}</td>
                    <td className="p-2 font-medium">{p.name}</td>
                    <td className="p-2">{p.zoneName}</td>
                    <td className="p-2">
                      {p.opensAt}–{p.closesAt}
                    </td>
                    <td className="p-2">{p.licenceNo || "—"}</td>
                    <td className="p-2">{p.priceCount ?? 0}</td>
                    <td className="p-2">
                      <Badge variant={p.active ? "secondary" : "outline"}>{p.active ? "Active" : "Inactive"}</Badge>
                    </td>
                    <td className="p-2">
                      <div className="flex items-center gap-1">
                        <Link href={`/ops/pharmacies/${p.id}/prices`}>
                          <Button size="sm" variant="outline">
                            Prices
                          </Button>
                        </Link>
                        <Button size="sm" variant="outline" onClick={() => edit(p)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" disabled={toggle.isPending} onClick={() => toggle.mutate(p)}>
                          {p.active ? "Deactivate" : "Activate"}
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive" disabled={remove.isPending} onClick={() => askDelete(p)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
